import axios from "axios";
import { signOut } from "firebase/auth";
import { auth } from "@/firebase/config";
import * as sessionTokens from "@/auth/sessionTokens";
import { showAlertModal } from "@/utils";
import store from "@/store/index";

export default {
    namespaced: true,
    state: {
        user: null,
        accessToken: sessionTokens.getAccessToken() || null,
    },
    mutations: {
        SET_USER(state, user) {
            state.user = user;
        },
        SET_ACCESS_TOKEN(state, token) {
            state.accessToken = token;
            sessionTokens.setAccessToken(token);
            sessionTokens.clearLegacyJwtLocalStorage();
        },
        LOGOUT(state) {
            state.user = null;
            state.accessToken = null;
            sessionTokens.clearSessionTokens();
            sessionTokens.clearLegacyJwtLocalStorage();
        },
    },
    actions: {
        async register({ dispatch }, formData) {
            try {
                // If formData is a FormData instance, Axios will handle it correctly
                return await axios.post(
                    `${process.env.VUE_APP_API_URL}/auth/register/`,
                    formData,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                    }
                );
            } catch (error) {
                console.error(
                    "Registration failed",
                    error.response?.status,
                    error.response?.data ?? error.message
                );
                showAlertModal(
                    store,
                    "Registration failed",
                    "danger",
                    12000,
                    error
                );
                return error;
            }
        },

        async login({ commit }, credentials) {
            delete axios.defaults.headers.common.Authorization;

            try {
                const { data } = await axios.post(
                    `${process.env.VUE_APP_API_URL}/auth/login/`,
                    credentials
                );
                commit("SET_ACCESS_TOKEN", data.access);
                sessionTokens.setRefreshToken(data.refresh);
                axios.defaults.headers.common["Authorization"] = `Bearer ${data.access}`;
                return data;
            } catch (error) {
                console.error(
                    "Login failed",
                    error.response?.status,
                    error.response?.data ?? error.message
                );
                return error;
            }
        },
        async firebaseLogin({ commit }, idToken) {
            delete axios.defaults.headers.common.Authorization;

            try {
                const { data } = await axios.post(
                    `${process.env.VUE_APP_API_URL}/auth/firebase-login/`,
                    { id_token: idToken }
                );
                commit("SET_ACCESS_TOKEN", data.access);
                sessionTokens.setRefreshToken(data.refresh);
                axios.defaults.headers.common["Authorization"] = `Bearer ${data.access}`;
                return data;
            } catch (error) {
                console.error(
                    "Firebase login failed",
                    error.response?.status,
                    error.response?.data ?? error.message
                );
                return error;
            }
        },
        async logout({ commit }) {
            const refresh = sessionTokens.getRefreshToken();

            // no refresh token in storage → nothing to blacklist
            if (!refresh) {
                commit("LOGOUT");
                delete axios.defaults.headers.common.Authorization;
                await signOut(auth).catch(() => {});
                return;
            }

            try {
                await axios.post(
                    `${process.env.VUE_APP_API_URL}/auth/logout/`,
                    { refresh }
                );
            } catch (error) {
                console.error(
                    "Logout failed",
                    error.response?.status,
                    error.response?.data ?? error.message
                );
            } finally {
                commit("LOGOUT");
                delete axios.defaults.headers.common.Authorization;
                // Clear Firebase Auth state so next login (e.g. after password reset) works
                await signOut(auth).catch(() => {});
            }
        },
    },
};
